import { createSlice, createSelector } from "@reduxjs/toolkit";
import { coreApi } from "api/core";
import { setShowMessage } from "redux/slices/uiSlice";

const baseUrl = "/permissions";
const initialState = {
  permissions: [],
  permission: {},
  modules: [],
  loading: false,
};

const permissionsSlice = createSlice({
  name: "permissions",
  initialState,
  reducers: {
    setPermission: (state, action) => {
      state.permission = action.payload;
    },
    setPermissions: (state, action) => {
      state.permissions = action.payload;
    },
    setLoading: (state) => {
      state.loading = true;
    },
    setReady: (state) => {
      state.loading = false;
    },
    setModules: (state, action) => {
      state.modules = action.payload;
    },
    clearPermissions: (state) => {
      state.permissions = [];
      state.permission = null;
    },
  },
});

export const {
  setPermission,
  setPermissions,
  setLoading,
  setReady,
  clearPermissions,
  setModules
} = permissionsSlice.actions;

export default permissionsSlice.reducer;

// get all permissions
export const getAllPermissions = () => async (dispatch) => {
  dispatch(setLoading());

  try {
    const permissions = await coreApi.fetch(baseUrl, "GET");
    dispatch(setPermissions(permissions));
  } catch (err) {
    console.error(err);
    dispatch(
      setShowMessage({
        description: "Failed to fetch permissions",
        type: "error",
      })
    );
  } finally {
    dispatch(setReady());
  }
};

// get permission by Id
export const getPermissionById = (id) => async (dispatch) => {
  dispatch(setLoading());

  try {
    const url = baseUrl + `/${id}`;
    const permission = await coreApi.fetch(url, "GET");
    dispatch(setPermission(permission));
  } catch (err) {
    console.error(err);
    dispatch(
      setShowMessage({
        description: "Failed to fetch permission",
        type: "error",
      })
    );
  } finally {
    dispatch(setReady());
  }
};

// add permission
export const addPermission = (payload) => async (dispatch) => {
  dispatch(setLoading());

  try {
    const res = await coreApi.post(baseUrl, payload);
    dispatch(
      setShowMessage({
        description: "Permission created successfully",
        type: "success",
      })
    );
    return res;
  } catch (err) {
    if (err.response?.status === 409) {
      dispatch(
        setShowMessage({
          description: "Permission already exists",
          type: "error",
        })
      );
    } else {
      dispatch(
        setShowMessage({
          description: "Failed to create permission",
          type: "error",
        })
      );
    }
  } finally {
    dispatch(setReady());
  }
};

// Update a permission
export const updatePermission = (id, payload) => async (dispatch) => {
  dispatch(setLoading());

  try {
    const url = baseUrl + `/${id}`;
    const res = await coreApi.put(url, payload);
    dispatch(
      setShowMessage({
        description: "Permission updated successfully",
        type: "success",
      })
    );
    return res;
  } catch (err) {
    if (err.response?.status === 409) {
      dispatch(
        setShowMessage({
          description: "Permission already exists",
          type: "error",
        })
      );
    } else {
      dispatch(
        setShowMessage({
          description: "Failed to update permission",
          type: "error",
        })
      );
    }
  } finally {
    dispatch(setReady());
  }
};
export const getAllModules = () => async (dispatch) => {
  dispatch(setLoading());

  try {
    const modules = await coreApi.fetch(`${baseUrl}/modules`, "GET");
    dispatch(setModules(modules));
  } catch (err) {
    console.error(err);
    dispatch(
      setShowMessage({
        description: "Failed to fetch modules",
        type: "error",
      })
    );
  } finally {
    dispatch(setReady());
  }
};

// Selectors
const permissionsSelector = ({ permissions }) => permissions?.permissions || [];
const permissionSelector = ({ permissions }) => permissions?.permission || {};
const permissionsStatusSelector = ({ permissions }) => permissions.loading;
const modulesSelector = ({ permissions }) => permissions?.modules || [];

export const selectModules = createSelector(modulesSelector, (modules) => modules);
export const selectPermissions = createSelector(permissionsSelector, (permissions) => permissions);
export const selectPermission = createSelector(permissionSelector, (permission) => permission);
export const selectPermissionsStatus = createSelector(permissionsStatusSelector, (loading) => loading);